import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut, LayoutDashboard, CreditCard, ChevronDown } from 'lucide-react';
import { supabase } from '../../../lib/supabase/client';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';

interface UserMenuProps {
  onItemClick?: () => void;
}

export function UserMenu({ onItemClick }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useOnClickOutside(menuRef, () => setIsOpen(false));

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onItemClick?.();
  };

  const handleMenuItemClick = () => {
    setIsOpen(false);
    onItemClick?.();
  };

  const menuItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/pricing', label: 'Pricing', icon: CreditCard }
  ];

  return (
    <div className="relative w-full lg:w-auto" ref={menuRef}>
      {/* Desktop User Menu */}
      <div className="hidden lg:block">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-500 to-blue-600 flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/20 py-2 z-[100]">
            {menuItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100/80 transition-colors"
                onClick={handleMenuItemClick}
              >
                <item.icon className="w-4 h-4 text-gray-500" />
                {item.label}
              </Link>
            ))}
            <hr className="my-2 border-gray-200" />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* Mobile User Menu - Direct display, no dropdown needed */}
      <div className="lg:hidden space-y-3 w-full">
        {menuItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={handleMenuItemClick}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 w-full
              ${location.pathname === item.to 
                ? 'text-white bg-white/20 shadow-lg' 
                : 'text-white/80 hover:text-white hover:bg-white/10'
              }
            `}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </Link>
        ))}
        
        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-all duration-200"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}